<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import type { TIcons } from '@/components/icon/types'
  import type { TButtonSize, TButtonType, TButtonVariant } from '@/components/ui/button/types'

  const props = withDefaults(
    defineProps<{
      title?: string
      variant?: TButtonVariant
      icon?: TIcons | null
      iconPosition?: 'left' | 'right'
      iconFilled?: boolean
      iconSize?: 'xxs' | 'xs' | 'sm' | 'md' | 'ml' | 'lg'
      iconBouncing?: boolean
      size?: TButtonSize
      disabled?: boolean
      type?: TButtonType
      active?: boolean
    }>(),
    {
      title: '',
      variant: 'primary',
      icon: null,
      iconPosition: 'left',
      iconFilled: false,
      iconSize: 'md',
      iconBouncing: false,
      size: 'md',
      disabled: false,
      type: 'button',
      active: false
    }
  )

  const el = ref<HTMLButtonElement | null>(null)

  function focus() {
    el.value?.focus()
  }

  defineExpose({ focus })

  const hasIcon = computed(() => !!props.icon && !props.title)
  const hasPrependIcon = computed(() => !!props.icon && !!props.title)

  const buttonBase = computed(() =>
    cva(
      'cursor-pointer focus-visible:ring-bt-blue-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:text-bt-blue-ui/35 text-subtitle-1',
      {
        variants: {
          variant: {
            primary: 'hover:bg-bt-green-hover bg-bt-green text-bt-blue-dark',
            secondary: 'bg-bt-blue-ui text-white hover:bg-bt-blue-ui-light',
            secondaryNoBg: 'bg-transparent text-bt-blue-ui',
            secondaryDark: 'bg-bt-blue-ui-dark text-white hover:bg-bt-blue-ui-dark',
            outline: 'border-border border bg-transparent text-bt-blue-ui hover:bg-bt-blue-ui hover:text-white',
            secondaryOutline:
              'border-border border bg-transparent text-bt-blue-ui-dark hover:border-bt-blue-ui-dark hover:text-bt-blue-ui-dark',
            outlineSecondary:
              'border-bt-blue-ui border bg-transparent text-bt-blue-ui hover:bg-bt-blue-ui hover:text-white',
            link: 'bg-transparent text-bt-blue-ui hover:text-bt-blue-ui-light',
            underline:
              'block bg-transparent p-0 rounded-none text-bt-blue-ui-light text-subtitle-1 underline decoration-1 underline-offset-8 hover:text-bt-blue-ui',
            border: 'border-border border bg-transparent text-bt-blue-ui hover:border-bt-blue-ui',
            icon: 'bg-white text-bt-blue-dark',
            none: '',
            filter:
              'border-border border bg-transparent text-bt-blue-ui-dark hover:border-bt-blue-ui-dark hover:text-bt-blue-ui-dark'
          },
          disabled: {
            false: '',
            primary: 'disabled:bg-bt-green/35',
            secondary: 'disabled:bg-bt-blue-ui/35 disabled:text-white/35',
            secondaryNoBg: 'disabled:text-bt-blue-ui/35',
            secondaryDark: 'disabled:bg-bt-blue-ui-dark/35 disabled:text-white/35',
            outline: 'hover:bg-transparent disabled:hover:bg-transparent',
            outlineSecondary: 'disabled:text-bt-blue-ui/35 disabled:border-bt-blue-ui/35',
            border: 'disabled:text-bt-blue-ui/35',
            secondaryOutline: 'disabled:border-bt-blue-ui-dark/35 disabled:text-bt-blue-ui-dark/35',
            link: '',
            underline: '',
            filter: ''
          },
          hasPrependIcon: {
            true: 'flex items-center gap-2',
            false: ''
          },
          iconPosition: {
            left: '',
            right: 'flex-row-reverse'
          },
          rounded: {
            default: 'rounded-sm',
            icon: 'rounded-full'
          },
          sm: {
            default: 'py-1 px-4',
            icon: 'p-1'
          },
          md: {
            default: 'py-2 px-4',
            icon: 'p-2'
          },
          lg: {
            default: 'py-3 px-6',
            icon: 'p-3'
          },
          xl: {
            default: 'py-5 px-8',
            icon: 'p-5'
          },
          iconBouncing: {
            true: 'gap-4',
            false: ''
          },
          active: {
            true: '',
            false: ''
          }
        },
        compoundVariants: [
          {
            variant: 'filter',
            active: true,
            class: 'outline outline-solid outline-2 outline-bt-blue-dark'
          },
          {
            variant: 'filter',
            active: false,
            class: 'hover:outline hover:outline-solid hover:outline-1 hover:outline-bt-blue-dark'
          }
        ]
      }
    )({
      variant: hasIcon.value && props.variant !== 'outline' ? 'none' : props.variant,
      [props.size]: hasIcon.value ? 'icon' : 'default',
      hasPrependIcon: hasPrependIcon.value,
      iconPosition: hasPrependIcon.value ? props.iconPosition ?? 'left' : 'left',
      disabled: props.disabled ? props.variant : false,
      rounded: hasIcon.value ? (props.variant !== 'outline' ? 'default' : 'icon') : 'default',
      iconBouncing: props.iconBouncing,
      active: props.active
    })
  )
</script>

<template>
  <button
    ref="el"
    :class="buttonBase"
    :disabled="disabled"
    :type="type"
  >
    <Icon
      v-if="!!icon"
      :name="icon"
      :filled="iconFilled"
      :size="iconSize"
      :class="{ 'animate-icon-bounce': iconBouncing && !disabled }"
    />
    <template v-if="title">
      {{ title }}
    </template>
  </button>
</template>
